// @flow
import type {
  BaggageType as Baggage,
  Combination as Comb,
  Definition as Def,
  Conditions as Cond,
} from "@kiwicom/nitro/lib/records/Baggage"

import Price from "common/records/Price"
import Journey from "common/records/Journey"
import type { AdditionalServicesInput } from "../../additionalServices/types"
import { CATEGORY_API_CONVERTER, CONDITION_API_CONVERTER } from "booking/services/baggage/consts"

export type BaggageCategoryApi = "hand_bag" | "hold_bag"
export type BaggageSubCategoryApi = "personal_item" | "cabin_bag" | "hold_bag"
type BaggageGroup = "adult" | "child" | "infant"

export type Conditions = {
  passenger_groups: BaggageGroup[],
  is_priority: string[],
}

type Combination = {
  price: {
    amount: number,
    base: number,
    currency: string,
    service: number,
    service_flat: number,
  },
  indices: number[],
  conditions: Conditions,
  passengers?: number[], // Just for save booking purposes
}

type Definition = {
  category: BaggageSubCategoryApi,
  price: {
    amount: number,
    base: number,
    currency: string,
    service: number,
    service_flat: number,
  },
  restrictions: {
    dimensions_sum: ?number,
    height: number,
    length: number,
    weight: number,
    width: number,
  },
  conditions: Conditions,
}

export type Combinations = {
  hand_bag: Combination[],
  hold_bag: Combination[],
}

export type BaggageInput = {
  // Redefine definitions personalItem is actually personal_item in input
  definitions: {
    hand_bag: Definition[],
    hold_bag: Definition[],
  },
  combinations: Combinations,
}

const mapConditions = (conditions: Conditions): Cond =>
  Object.keys(conditions).reduce((acc, key) => {
    // $FlowFixMe
    acc[CONDITION_API_CONVERTER[key]] = conditions[key]
    return acc
  }, {})

const mapCombination = (combination: Combination, index: number): Comb => ({
  index,
  price: new Price({
    amount: combination.price.amount,
    base: combination.price.base,
    service: combination.price.service,
    serviceFlat: combination.price.service_flat,
    currency: combination.price.currency,
  }),
  indices: combination.indices,
  conditions: mapConditions(combination.conditions),
})

const mapDefinition = (definition: Definition, index: number): Def => ({
  index,
  category: CATEGORY_API_CONVERTER[definition.category],
  price: new Price({
    amount: definition.price.amount,
    base: definition.price.base,
    service: definition.price.service,
    serviceFlat: definition.price.service_flat,
    currency: definition.price.currency,
  }),
  restrictions: {
    dimensionsSum: definition.restrictions.dimensions_sum,
    height: definition.restrictions.height,
    length: definition.restrictions.length,
    weight: definition.restrictions.weight,
    width: definition.restrictions.width,
  },
  conditions: mapConditions(definition.conditions),
})

const calculatePriority = (priorityAirlines: string[], journey, prioBoardingData) => {
  if (priorityAirlines.length <= 0) {
    return new Price({ amount: 0 })
  }
  const prices = priorityAirlines.map(airline => {
    const flightsPrices = journey
      .getFlightsByAirline(airline)
      .map(flight => flight.id)
      // $FlowFixMe
      .toArray()
      .map(flightId => {
        if (!prioBoardingData[flightId]) {
          return null
        }
        const priceInfo = prioBoardingData[flightId].price_info
        return new Price({
          amount: Number(priceInfo.amount),
          base: Number(priceInfo.base),
          currency: priceInfo.currency,
          merchant: Number(priceInfo.merchant),
          service: Number(priceInfo.service),
          serviceFlat: Number(priceInfo.service_flat),
        })
      })
      .filter(Boolean)
    if (flightsPrices.length <= 0) {
      return new Price({ amount: 0 })
    }
    return Price.sum(flightsPrices)
  })
  return Price.sum(prices)
}

const mapHandBagDefinition = (journey, prioBoardingData) => (
  definition: Definition,
  index: number,
): Def => {
  const priorityAirlines = definition.conditions.is_priority || []
  if (priorityAirlines.length <= 0) {
    // No need for priority price
    return mapDefinition(definition, index)
  }
  const priorityPrice = calculatePriority(priorityAirlines, journey, prioBoardingData)
  const def = mapDefinition(definition, index)
  return { ...def, price: Price.sum([def.price, priorityPrice]) }
}

const mapHandBagCombination = (journey, prioBoardingData) => (
  combination: Combination,
  index: number,
): Comb => {
  const priorityAirlines = combination.conditions.is_priority || []
  if (priorityAirlines.length <= 0) {
    // No need for priority price
    return mapCombination(combination, index)
  }
  const priorityPrice = calculatePriority(priorityAirlines, journey, prioBoardingData)
  const comb = mapCombination(combination, index)
  return { ...comb, price: Price.sum([comb.price, priorityPrice]) }
}

const mapperBaggage = (
  input: BaggageInput,
  journey: Journey,
  additionalServices: ?AdditionalServicesInput,
): Baggage => {
  const priorityBoarding = additionalServices ? additionalServices.priority_boarding : null
  const prioBoardingData = priorityBoarding ? priorityBoarding.data : {}

  return {
    definitions: {
      handBag: input.definitions.hand_bag.map(mapHandBagDefinition(journey, prioBoardingData)),
      holdBag: input.definitions.hold_bag.map(mapDefinition),
    },
    combinations: {
      handBag: input.combinations.hand_bag.map(mapHandBagCombination(journey, prioBoardingData)),
      holdBag: input.combinations.hold_bag.map(mapCombination),
    },
    original: input,
  }
}

export default mapperBaggage
